import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import clientsService from "../services/clientsService";
import s3Service from "../services/s3Service";
import {
  validateDocumentFile,
  getDocumentAcceptValue,
} from "../utils/documentValidation";
import styles from "./PropertyDocumentCreate.module.css"; // Reuse property document styles

const ClientDocumentCreate = () => {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    document_type_id: "",
    document: null,
    document_key: null,
    document_name: null,
    document_type: null,
    document_size: null,
    visible_to_client: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [clientDocumentTypes, setClientDocumentTypes] = useState({});

  // Load client data and document types
  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await clientsService.getClientDocumentFormData(clientId);
      if (response.success) {
        setClient(response.data.client);
        // Convert document types object to format expected by select
        const typesObj = response.data.clientDocumentTypes || {};
        setClientDocumentTypes(typesObj);
      } else {
        throw new Error("Failed to load client data");
      }
    } catch (error) {
      console.error("Error loading client data:", error);
      setError("Error loading client data");
      toast.error("Error loading client data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        document: null,
        document_key: null,
        document_name: null,
        document_type: null,
        document_size: null,
      }));
      setUploadProgress(0);
      return;
    }

    // Validate file
    const validationResult = validateDocumentFile(file);
    if (!validationResult.isValid) {
      toast.error(validationResult.error);
      e.target.value = "";
      return;
    }

    // Start upload process
    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload file to S3
      // Laravel S3 path structure: private/client/{collection-slug}/{media-id}/{filename}
      // Matching Laravel MediaPathResolver + private disk: private/client/{collection-slug}/{media-id}/{filename}
      // Collection slug = document type name slugged (e.g., "completed-client-objection-handling-form")
      // Media ID = document ID (will be generated after document creation, use timestamp for now)
      // Filename = original filename
      // Note: Laravel uses 'private' disk (useDisk('private')) which adds 'private/' prefix in S3
      
      // Get document type name and slug it (matching Laravel str_slug)
      const documentTypeName = clientDocumentTypes[formData.document_type_id] || `Document Type ${formData.document_type_id}`;
      const collectionSlug = documentTypeName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      // Use timestamp as media ID (will be replaced with actual document ID after creation)
      const mediaId = Date.now();
      
      // Laravel-compatible path structure with private disk prefix
      const folder = `private/client/${collectionSlug}/${mediaId}`;
      
      const s3Key = await s3Service.uploadDocument(file, {
        folder: folder,
        useLaravelStructure: true, // Use Laravel path structure (no timestamp prefix)
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      // Update form data with S3 information
      setFormData((prev) => ({
        ...prev,
        document: file,
        document_key: s3Key,
        document_name: file.name,
        document_type: file.type,
        document_size: file.size,
      }));

      // Clear error when upload succeeds
      if (formErrors.document) {
        setFormErrors((prev) => ({
          ...prev,
          document: "",
        }));
      }

      toast.success("Document uploaded successfully!");
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Failed to upload document");
      e.target.value = "";
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.document_type_id) {
      errors.document_type_id = "Document type is required";
    }

    if (!formData.document_key) {
      errors.document = "Document is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      // Create JSON data with S3 key information
      const submitData = {
        client_id: client.id,
        name: formData.name,
        document_type_id: formData.document_type_id,
        document_key: formData.document_key,
        document_name: formData.document_name,
        document_type: formData.document_type,
        document_size: formData.document_size,
        visible_to_client: formData.visible_to_client ? 1 : 0,
      };

      console.log("🚀 [FRONTEND] Submitting client document data:", submitData);

      // API call to create document
      const response = await clientsService.createClientDocument(
        clientId,
        submitData
      );

      if (response.success) {
        toast.success("Document added successfully!");
        navigate(`/clients/${clientId}`);
      } else {
        throw new Error(response.message || "Failed to add document");
      }
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error(error.message || "Error creating document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/clients/${clientId}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleCancel} className={styles.btnSecondary}>
            Back to Client
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Clients
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          / {client?.name}
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Add Document</span>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Add document to client</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.fieldset}>
                  <input
                    type="hidden"
                    name="client_id"
                    value={client?.id}
                  />

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="name"
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="Enter document name"
                      />
                      {formErrors.name && (
                        <span className={styles.errorText}>
                          {formErrors.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="document_type_id"
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Document Type
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        id="document_type_id"
                        name="document_type_id"
                        value={formData.document_type_id}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      >
                        <option value="">Please Select</option>
                        {Object.entries(clientDocumentTypes).map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                      </select>
                      {formErrors.document_type_id && (
                        <span className={styles.errorText}>
                          {formErrors.document_type_id}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="document"
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Document
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.inputImage}>
                        <input
                          type="file"
                          id="document"
                          name="document"
                          onChange={handleFileChange}
                          className={styles.formControl}
                          accept={getDocumentAcceptValue()}
                          disabled={uploading}
                        />
                        {uploading && (
                          <div className={styles.uploadProgress}>
                            <div className={styles.progressBar}>
                              <div 
                                className={styles.progressFill}
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                            <span className={styles.progressText}>
                              Uploading... {Math.round(uploadProgress)}%
                            </span>
                          </div>
                        )}
                        {formData.document_name && !uploading && (
                          <div className={styles.uploadSuccess}>
                            <span className={styles.successIcon}>✓</span>
                            <span className={styles.successText}>
                              {formData.document_name} uploaded successfully
                            </span>
                          </div>
                        )}
                        {formErrors.document && (
                          <span className={styles.errorText}>
                            {formErrors.document}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label
                      htmlFor="visible_to_client"
                      className={styles.controlLabel}
                    >
                      Visible to client
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="checkbox"
                        id="visible_to_client"
                        name="visible_to_client"
                        checked={formData.visible_to_client}
                        onChange={handleInputChange}
                        className={styles.checkbox}
                      />
                      {formErrors.visible_to_client && (
                        <span className={styles.errorText}>
                          {formErrors.visible_to_client}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>
                </fieldset>

                <div className={styles.formGroup}>
                  <div className={styles.textRight}>
                    <button
                      type="submit"
                      className={styles.btnHead}
                      disabled={submitting}
                    >
                      <i className={styles.glyphiconPlus}></i>
                      {submitting ? "Adding document..." : "Add document"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDocumentCreate;

